// =============================================================================
// GOUV SECURITY - Widget Gouvernance : Sécurité & RBAC
// Migré depuis management/RBACManager.jsx le 22/02/2026
// Architecture: Users | Feature Flags (Produit) | Rôles (Organisation) | Permissions (Actions)
// =============================================================================

import { useState, useEffect } from "react";
import {
  Users,
  UserPlus,
  UserCog,
  Shield,
  Settings,
  Layers,
  Building2,
  ChevronRight,
  ChevronDown,
  ToggleLeft,
  ToggleRight,
  Edit,
  Save,
  X,
  Loader2,
  Check,
  RefreshCw,
  Trash2,
  Mail,
  Search,
  User,
  AlertCircle,
  Plus,
  Eye,
  EyeOff,
  Key,
  History,
  LogIn
} from "lucide-react";
import GouvLoginHistory from "./GouvLoginHistory";
import { assignableRolesFor } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { Checkbox } from "../../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Actions disponibles (standard + critiques)
const ACTIONS = ["read", "create", "update", "delete", "transition", "validate", "lock", "archive", "pay"];
const CRITICAL_ACTIONS = ["lock", "archive", "pay"]; // Actions irréversibles
const ACTION_LABELS = {
  read: "Lecture",
  create: "Créer",
  update: "Modifier",
  delete: "Supprimer",
  transition: "Transition",
  validate: "Valider",
  lock: "Verrouiller",
  archive: "Archiver",
  pay: "Paiement"
};

// Couleurs des rôles
const ROLE_COLORS = {
  super_admin: "text-amber-400 bg-amber-500/20",
  admin: "text-blue-400 bg-blue-500/20",
  manager: "text-purple-400 bg-purple-500/20",
  user: "text-zinc-400 bg-zinc-500/20",
  external: "text-emerald-400 bg-emerald-500/20"
};

export default function GouvSecurity({ currentUser, currentTenant, tenants = [] }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users"); // users | flags | roles | permissions | overrides
  
  // Utilisateurs
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  
  // Feature Flags
  const [featureFlags, setFeatureFlags] = useState({});
  const [availableFeatures, setAvailableFeatures] = useState([]);
  
  // Rôles et Permissions
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  const [editingPermissions, setEditingPermissions] = useState(false);
  const [hubsDefinition, setHubsDefinition] = useState({});
  
  // Installateurs (pour lien user-installateur)
  const [installateurs, setInstallateurs] = useState([]);
  
  // Surcharges utilisateur
  const [selectedUserForOverride, setSelectedUserForOverride] = useState(null);
  const [userOverrides, setUserOverrides] = useState({});
  const [editingOverrides, setEditingOverrides] = useState(false);
  
  // Historique de connexion
  const [historyUser, setHistoryUser] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Feature Flags par Rôle (pour Admin)
  const [roleFeatures, setRoleFeatures] = useState({});
  const [selectedRoleForFlags, setSelectedRoleForFlags] = useState(null);
  const [editingRoleFlags, setEditingRoleFlags] = useState(false);
  const [flagsSubTab, setFlagsSubTab] = useState("tenant"); // tenant | roles
  
  // Tenant sélectionné - utiliser le SLUG (pas l'ObjectId)
  const [selectedTenantId, setSelectedTenantId] = useState(currentTenant?.slug || currentTenant?.id || "");

  // Provisioning : liste COMPLÈTE des tenants plateforme (super_admin uniquement).
  // /user/tenants (prop `tenants`) est membership-only (décision 26/05) → un
  // super_admin par FLAG sans membership y arrive vide. Pour CET écran d'admin
  // produit, on charge tous les tenants via GET /api/platform/tenants.
  const [allTenants, setAllTenants] = useState([]);

  const isSuperAdmin = currentUser?.rôle === "super_admin" || currentUser?.is_super_admin;
  const isAdmin = currentUser?.rôle === "admin" || isSuperAdmin;

  // Source du sélecteur de tenant : super_admin → TOUS les tenants (provisioning) ;
  // sinon → prop `tenants` (membership de l'utilisateur courant, inchangé).
  const tenantOptions = isSuperAdmin && allTenants.length > 0 ? allTenants : tenants;
  
  // Mettre à jour selectedTenantId quand currentTenant change
  useEffect(() => {
    if (currentTenant?.slug && selectedTenantId !== currentTenant.slug) {
      setSelectedTenantId(currentTenant.slug);
    } else if (currentTenant?.id && !selectedTenantId) {
      setSelectedTenantId(currentTenant.id);
    }
  }, [currentTenant]);
  
  // Headers avec token
  const getHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : {};
  };

  // Charger les données
  useEffect(() => {
    if (selectedTenantId) {
      loadData();
    }
  }, [selectedTenantId]);

  // Provisioning : charger TOUS les tenants pour le sélecteur (super_admin).
  // On ne touche PAS /user/tenants (membership-only, décision 26/05) — on ajoute
  // juste cette source dédiée à la config produit super_admin dans cet écran.
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/platform/tenants`, { headers: getHeaders(), credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : (data.tenants || []);
          setAllTenants(list);
          // Super_admin par flag sans membership → pas de currentTenant : prendre le 1er.
          setSelectedTenantId(prev => prev || list[0]?.slug || list[0]?.id || "");
        }
      } catch (err) {
        console.error("Erreur chargement tenants (provisioning):", err);
      }
    })();
  }, [isSuperAdmin]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      
      // Charger les utilisateurs
      const usersRes = await fetch(`${API}/api/hub/users`, { headers, credentials: "include" });
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }
      
      // Charger les installateurs
      const installRes = await fetch(`${API}/api/operation/installateurs`, { headers, credentials: "include" });
      if (installRes.ok) {
        const data = await installRes.json();
        setInstallateurs(Array.isArray(data) ? data : data.items || []);
      }
      
      // Charger la définition RBAC (hubs, modules, actions)
      const defRes = await fetch(`${API}/api/platform/rbac/definition`, { headers, credentials: "include" });
      if (defRes.ok) {
        const data = await defRes.json();
        setHubsDefinition(data.hubs || {});
      }
      
      // Charger les Feature Flags
      const flagsRes = await fetch(`${API}/api/platform/features/${selectedTenantId}`, { headers, credentials: "include" });
      if (flagsRes.ok) {
        const data = await flagsRes.json();
        setFeatureFlags(data.features || {});
      }
      
      // Charger les features disponibles
      const availRes = await fetch(`${API}/api/platform/features/available`, { headers, credentials: "include" });
      if (availRes.ok) {
        const data = await availRes.json();
        setAvailableFeatures(data.features || []);
      }
      
      // Charger les rôles du tenant
      const rolesRes = await fetch(`${API}/api/platform/rbac/roles/${selectedTenantId}`, { headers, credentials: "include" });
      if (rolesRes.ok) {
        const data = await rolesRes.json();
        setRoles(data.roles || []);
      }
      
      // Charger les features par rôle (pour Admin)
      if (isAdmin) {
        const roleFlagsRes = await fetch(`${API}/api/platform/features/${selectedTenantId}/roles`, { headers, credentials: "include" });
        if (roleFlagsRes.ok) {
          const data = await roleFlagsRes.json();
          // Transformer en objet { role_id: features }
          const rolesFeatureMap = {};
          for (const rf of (data.roles || [])) {
            rolesFeatureMap[rf.role_id] = {
              features: rf.features,
              inherited: rf.inherited,
              role_name: rf.role_name
            };
          }
          setRoleFeatures(rolesFeatureMap);
        }
      }
    } catch (err) {
      console.error("Erreur chargement:", err);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  // ========== GESTION DES UTILISATEURS ==========
  
  // Filtrer les utilisateurs par recherche et tenant
  const filteredUsers = users.filter(u => {
    const matchSearch = !searchQuery || 
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Super Admin voit tous les users
    if (isSuperAdmin) return matchSearch;
    
    // Admin voit les users de son tenant
    if (isAdmin) {
      const userTenants = u.tenants || [];
      const userCurrentTenant = u.current_tenant;
      return matchSearch && (
        userTenants.includes(selectedTenantId) || 
        userCurrentTenant === selectedTenantId ||
        userTenants.length === 0 // Users sans tenant assigné
      );
    }
    
    return false;
  });

  // Ouvrir le dialog d'édition
  const openEditUser = (user) => {
    setEditingUser({
      _id: user._id,
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      rcs: user.rcs || "",
      rôle: user.role || user.rôle || "user",
      tenants: user.tenants || [],
      current_tenant: user.current_tenant || "",
      installateur_id: user.installateur_id || ""
    });
    setShowUserDialog(true);
  };

  // Créer un nouvel utilisateur
  const openNewUser = () => {
    setEditingUser({
      _id: null,
      name: "",
      email: "",
      phone: "",
      rcs: "",
      rôle: "user",
      tenants: [selectedTenantId],
      current_tenant: selectedTenantId,
      installateur_id: ""
    });
    setShowUserDialog(true);
  };

  // Sauvegarder l'utilisateur
  const handleSaveUser = async () => {
    if (!editingUser.email || !editingUser.name) {
      toast.error("Nom et email requis");
      return;
    }
    
    const isNew = !editingUser._id;
    // Déterminer le tenant cible
    const targetTenant = isSuperAdmin ? (editingUser.current_tenant || selectedTenantId) : selectedTenantId;
    
    if (isNew && !targetTenant) {
      toast.error("Sélectionnez un tenant");
      return;
    }
    
    try {
      const method = editingUser._id ? "PUT" : "POST";
      const url = editingUser._id 
        ? `${API}/api/hub/users/${editingUser._id}`
        : `${API}/api/hub/users`;
      
      const payload = {
        name: editingUser.name,
        email: editingUser.email,
        phone: editingUser.phone,
        rcs: editingUser.rcs || null,
        role: editingUser.rôle,
        tenants: [targetTenant],
        current_tenant: targetTenant,
        installateur_id: editingUser.installateur_id || null,
        ...(editingUser.password && { password: editingUser.password }),
        ...(isNew && { send_invitation: true, base_url: window.location.origin })
      };
      
      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        if (isNew && data.invitation_sent) {
          toast.success(`Invitation envoyée à ${editingUser.email}`);
        } else if (isNew) {
          toast.success(`Utilisateur créé (invitation non envoyée — vérifiez la config SMTP)`);
        } else {
          toast.success("Utilisateur modifié");
        }
        setShowUserDialog(false);
        setEditingUser(null);
        loadData();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Erreur lors de l'enregistrement");
      }
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // Charger l'historique de connexion d'un user et ouvrir le dialog
  const openLoginHistory = async (user) => {
    const uid = user.user_id || user._id || user.email;
    setHistoryUser(user);
    setHistoryData(null);
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API}/api/hub/users/${encodeURIComponent(uid)}/login-history?limit=200`, {
        headers: getHeaders(),
        credentials: "include"
      });
      if (res.ok) {
        setHistoryData(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur de chargement de l'historique");
      }
    } catch (err) {
      toast.error("Erreur réseau");
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatLoginTs = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      });
    } catch { return iso; }
  };

  // Supprimer un utilisateur
  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    
    try {
      const res = await fetch(`${API}/api/hub/users/${userId}`, {
        method: "DELETE",
        headers: getHeaders(),
        credentials: "include"
      });
      
      if (res.ok) {
        toast.success("Utilisateur supprimé");
        loadData();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Erreur lors de la suppression");
      }
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // ==========================================================================
  // SURCHARGES UTILISATEUR
  // ==========================================================================

  // Charger les surcharges d'un utilisateur
  const loadUserOverrides = async (userId) => {
    try {
      const res = await fetch(
        `${API}/api/platform/rbac/users/${selectedTenantId}/${userId}/overrides`,
        { headers: getHeaders(), credentials: "include" }
      );
      
      if (res.ok) {
        const data = await res.json();
        setUserOverrides(data.overrides || {});
        setSelectedUserForOverride(users.find(u => (u.user_id || u.email) === userId) || { user_id: userId });
      }
    } catch (err) {
      console.error("Erreur chargement surcharges:", err);
      toast.error("Erreur lors du chargement des surcharges");
    }
  };

  // ========== FEATURE FLAGS PAR RÔLE ==========

  // Toggle une feature pour un rôle
  const toggleRoleFeature = async (roleId, featureId, enabled) => {
    try {
      const res = await fetch(
        `${API}/api/platform/features/${selectedTenantId}/roles/${roleId}/toggle`,
        {
          method: "POST",
          headers: getHeaders(),
          credentials: "include",
          body: JSON.stringify({
            feature_id: featureId,
            enabled,
            updated_by: currentUser?.email
          })
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Feature ${enabled ? 'activée' : 'désactivée'} pour ce rôle`);
        
        // Mettre à jour localement avec la cascade
        setRoleFeatures(prev => ({
          ...prev,
          [roleId]: {
            ...prev[roleId],
            features: data.features,
            inherited: false
          }
        }));
      } else {
        toast.error("Erreur lors de la mise à jour");
      }
    } catch (err) {
      console.error("Erreur toggle rôle feature:", err);
      toast.error("Erreur de connexion");
    }
  };

  // Sauvegarder une surcharge
  const saveUserOverride = async (hub, module, actions) => {
    const userId = selectedUserForOverride?.user_id || selectedUserForOverride?.email;
    if (!userId) return;
    
    try {
      const res = await fetch(
        `${API}/api/platform/rbac/users/${selectedTenantId}/${userId}/permissions/override`,
        {
          method: "POST",
          headers: getHeaders(),
          credentials: "include",
          body: JSON.stringify({
            hub,
            module,
            actions,
            set_by: currentUser?.email
          })
        }
      );
      
      if (res.ok) {
        toast.success("Surcharge enregistrée");
        // Mettre à jour localement
        setUserOverrides(prev => ({
          ...prev,
          [hub]: {
            ...(prev[hub] || {}),
            [module]: actions
          }
        }));
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // Supprimer une surcharge de module
  const clearUserOverrideModule = async (hub, module) => {
    const userId = selectedUserForOverride?.user_id || selectedUserForOverride?.email;
    if (!userId) return;
    
    try {
      const res = await fetch(
        `${API}/api/platform/rbac/users/${selectedTenantId}/${userId}/overrides/${hub}/${module}`,
        {
          method: "DELETE",
          headers: getHeaders(),
          credentials: "include"
        }
      );
      
      if (res.ok) {
        toast.success("Surcharge supprimée");
        // Mettre à jour localement
        setUserOverrides(prev => {
          const newOverrides = { ...prev };
          if (newOverrides[hub]) {
            delete newOverrides[hub][module];
            if (Object.keys(newOverrides[hub]).length === 0) {
              delete newOverrides[hub];
            }
          }
          return newOverrides;
        });
      }
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Supprimer toutes les surcharges d'un utilisateur
  const clearAllUserOverrides = async () => {
    const userId = selectedUserForOverride?.user_id || selectedUserForOverride?.email;
    if (!userId) return;
    
    if (!window.confirm("Supprimer toutes les surcharges de cet utilisateur ?")) return;
    
    try {
      const res = await fetch(
        `${API}/api/platform/rbac/users/${selectedTenantId}/${userId}/overrides`,
        {
          method: "DELETE",
          headers: getHeaders(),
          credentials: "include"
        }
      );
      
      if (res.ok) {
        toast.success("Toutes les surcharges supprimées");
        setUserOverrides({});
      }
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Toggle une action dans les surcharges
  const toggleOverrideAction = (hub, module, action) => {
    setUserOverrides(prev => {
      const currentActions = prev[hub]?.[module] || [];
      const newActions = currentActions.includes(action)
        ? currentActions.filter(a => a !== action)
        : [...currentActions, action];
      
      return {
        ...prev,
        [hub]: {
          ...(prev[hub] || {}),
          [module]: newActions
        }
      };
    });
  };

  // Vérifier si une action est surchargée
  const isActionOverridden = (hub, module, action) => {
    const actions = userOverrides[hub]?.[module] || [];
    return actions.includes(action);
  };

  // Toggle Feature Flag
  const handleToggleFlag = async (featureId, enabled) => {
    if (!isSuperAdmin) {
      toast.error("Seul le Super Admin peut modifier les Feature Flags");
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/platform/features/${selectedTenantId}/toggle`, {
        method: "POST",
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify({
          feature_id: featureId,
          enabled: enabled,
          updated_by: currentUser?.email
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setFeatureFlags(data.features || {});
        toast.success(`Feature ${enabled ? 'activée' : 'désactivée'}`);
        
        // Déclencher un événement pour que le PermissionContext se rafraîchisse
        window.dispatchEvent(new CustomEvent('feature-flags-changed'));
      }
    } catch (err) {
      toast.error("Erreur lors de la modification");
    }
  };

  // Charger les permissions d'un rôle
  const loadRolePermissions = async (roleId) => {
    try {
      const res = await fetch(`${API}/api/platform/rbac/roles/${selectedTenantId}/${roleId}/permissions`, {
        headers: getHeaders(),
        credentials: "include"
      });
      
      if (res.ok) {
        const data = await res.json();
        setRolePermissions(data.permissions || {});
        setSelectedRole(roleId);
      }
    } catch (err) {
      toast.error("Erreur chargement permissions");
    }
  };

  // Sauvegarder les permissions du rôle
  const saveRolePermissions = async () => {
    try {
      const res = await fetch(`${API}/api/platform/rbac/roles/${selectedTenantId}/${selectedRole}/permissions`, {
        method: "POST",
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify({
          permissions: rolePermissions,
          updated_by: currentUser?.email
        })
      });
      
      if (res.ok) {
        toast.success("Permissions enregistrées");
        setEditingPermissions(false);
        loadData();
      }
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // Toggle une permission dans la matrice
  const togglePermission = (hub, module, action) => {
    setRolePermissions(prev => {
      const newPerms = { ...prev };
      if (!newPerms[hub]) newPerms[hub] = {};
      if (!newPerms[hub][module]) newPerms[hub][module] = [];
      
      const actions = [...newPerms[hub][module]];
      const idx = actions.indexOf(action);
      
      if (idx >= 0) {
        actions.splice(idx, 1);
      } else {
        actions.push(action);
      }
      
      newPerms[hub][module] = actions;
      return newPerms;
    });
  };

  // Vérifier si une action est activée
  const hasPermission = (hub, module, action) => {
    if (rolePermissions === "all" || rolePermissions === "all_tenant") return true;
    if (!rolePermissions || typeof rolePermissions !== "object") return false;
    
    const hubPerms = rolePermissions[hub];
    if (!hubPerms) return false;
    
    const modulePerms = hubPerms[module];
    if (!modulePerms) return false;
    
    return modulePerms.includes(action);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestion des Accès</h1>
          <p className="text-zinc-400">Feature Flags, Rôles et Permissions</p>
        </div>
        
        {/* Sélecteur de Tenant (Super Admin) */}
        {isSuperAdmin && tenantOptions.length > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-zinc-400 text-sm">Tenant :</span>
            <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
              <SelectTrigger className="w-64 bg-zinc-800 border-white/10">
                <SelectValue placeholder="Sélectionner un tenant" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10">
                {tenantOptions.map(t => (
                  <SelectItem key={t.slug || t.id} value={t.slug || t.id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {t.nom || t.name || t.slug}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={loadData} aria-label="Rafraîchir">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        <Button
          variant={activeTab === "users" ? "default" : "ghost"}
          onClick={() => setActiveTab("users")}
          className={activeTab === "users" ? "bg-cyan-500/20 text-cyan-400" : "text-zinc-400"}
        >
          <Users className="w-4 h-4 mr-2" />
          Utilisateurs ({filteredUsers.length})
        </Button>
        {isSuperAdmin && (
          <Button
            variant={activeTab === "flags" ? "default" : "ghost"}
            onClick={() => setActiveTab("flags")}
            className={activeTab === "flags" ? "bg-emerald-500/20 text-emerald-400" : "text-zinc-400"}
          >
            <Layers className="w-4 h-4 mr-2" />
            Feature Flags
          </Button>
        )}
        <Button
          variant={activeTab === "roles" ? "default" : "ghost"}
          onClick={() => setActiveTab("roles")}
          className={activeTab === "roles" ? "bg-purple-500/20 text-purple-400" : "text-zinc-400"}
        >
          <Shield className="w-4 h-4 mr-2" />
          Rôles
        </Button>
        <Button
          variant={activeTab === "permissions" ? "default" : "ghost"}
          onClick={() => { setActiveTab("permissions"); if (!selectedRole && roles.length > 0) loadRolePermissions(roles[0].id); }}
          className={activeTab === "permissions" ? "bg-blue-500/20 text-blue-400" : "text-zinc-400"}
        >
          <Settings className="w-4 h-4 mr-2" />
          Permissions
        </Button>
        <Button
          variant={activeTab === "overrides" ? "default" : "ghost"}
          onClick={() => setActiveTab("overrides")}
          className={activeTab === "overrides" ? "bg-amber-500/20 text-amber-400" : "text-zinc-400"}
        >
          <UserCog className="w-4 h-4 mr-2" />
          Surcharges
        </Button>
        <Button
          variant={activeTab === "login-history" ? "default" : "ghost"}
          onClick={() => setActiveTab("login-history")}
          className={activeTab === "login-history" ? "bg-emerald-500/20 text-emerald-400" : "text-zinc-400"}
          data-testid="security-tab-login-history"
        >
          <History className="w-4 h-4 mr-2" />
          Connexions
        </Button>
      </div>

      {/* ========== BLOC 5: HISTORIQUE CONNEXIONS ========== */}
      {activeTab === "login-history" && (
        <GouvLoginHistory users={users} />
      )}

      {/* ========== BLOC 0: UTILISATEURS ========== */}
      {activeTab === "users" && (
        <div className="space-y-4">
          {/* Barre de recherche et bouton Ajouter */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Rechercher un utilisateur..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-800 border-white/10"
              />
            </div>
            <Button onClick={openNewUser} className="bg-cyan-500 hover:bg-cyan-600">
              <UserPlus className="w-4 h-4 mr-2" />
              Nouvel utilisateur
            </Button>
          </div>

          {/* Liste des utilisateurs */}
          <Card className="bg-zinc-900/50 border-white/10">
            <CardContent className="p-0">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun utilisateur trouvé</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {filteredUsers.map(user => (
                    <div 
                      key={user._id}
                      className="flex items-center justify-between p-4 hover:bg-zinc-900/50/5 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        {/* Avatar */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${ROLE_COLORS[user.rôle] || ROLE_COLORS.user}`}>
                          {user.name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        
                        {/* Infos */}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white">{user.name || "Sans nom"}</p>
                            <Badge className={`text-xs ${ROLE_COLORS[user.rôle] || ROLE_COLORS.user}`}>
                              {user.rôle === "super_admin" ? "Super Admin" : 
                               user.rôle === "admin" ? "Admin" :
                               user.rôle === "manager" ? "Manager" : 
                               user.rôle === "external" ? "User Externe" : "User"}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-zinc-500">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </div>
                          {/* Tenants */}
                          {user.tenant_names?.length > 0 && (
                            <p className="text-xs text-zinc-600 mt-1">
                              Tenants: {user.tenant_names.join(", ")}
                            </p>
                          )}
                          {/* Installateur lié */}
                          {user.installateur_id && (
                            <p className="text-xs text-cyan-500/80 mt-1">
                              Installateur: {installateurs.find(i => (i.id || i._id) === user.installateur_id)?.entreprise || 
                                            installateurs.find(i => (i.id || i._id) === user.installateur_id)?.nom || 
                                            user.installateur_id}
                            </p>
                          )}
                          {user.rcs && (
                            <p className="text-xs text-purple-400/80 mt-0.5">
                              RCS: {user.rcs}
                            </p>
                          )}
                          {/* Connexions */}
                          <p className="text-xs text-zinc-500 mt-1 flex items-center gap-2">
                            <LogIn className="w-3 h-3" />
                            {(user.login_count || 0) === 0 ? (
                              <span className="text-zinc-600">Jamais connecté</span>
                            ) : (
                              <>
                                <span className="text-emerald-400/80">{user.login_count} connexion{user.login_count > 1 ? "s" : ""}</span>
                                {user.last_login_at && (
                                  <span className="text-zinc-500">— dernière {formatLoginTs(user.last_login_at)}</span>
                                )}
                              </>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Voir l'historique de connexion"
                          aria-label="Historique de connexion"
                          data-testid={`login-history-${user.user_id || user._id}`}
                          className="text-zinc-400 hover:text-emerald-400"
                          onClick={() => openLoginHistory(user)}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Renvoyer l'invitation"
                          data-testid={`resend-invite-${user.user_id || user._id}`}
                          className="text-zinc-400 hover:text-cyan-400"
                          onClick={async () => {
                            try {
                              const uid = user.user_id || user._id;
                              const res = await fetch(`${API}/api/hub/users/${uid}/resend-invitation`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json", ...(() => { const t = localStorage.getItem("session_token"); return t ? { Authorization: `Bearer ${t}` } : {}; })() },
                                credentials: "include",
                                body: JSON.stringify({ base_url: window.location.origin })
                              });
                              if (res.ok) toast.success(`Invitation envoyée à ${user.email}`);
                              else { const e = await res.json(); toast.error(e.detail || "Échec de l'envoi"); }
                            } catch { toast.error("Erreur réseau"); }
                          }}
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditUser(user)}
                          className="text-zinc-400 hover:text-white"
                          aria-label="Modifier l'utilisateur"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {user.email !== currentUser?.email && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteUser(user._id)}
                            className="text-zinc-400 hover:text-red-400"
                            aria-label="Supprimer l'utilisateur"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ========== BLOC 1: FEATURE FLAGS ========== */}
      {activeTab === "flags" && (
        <div className="space-y-6">
          {/* Sous-onglets: Tenant | Rôles */}
          {isAdmin && (
            <div className="flex gap-2 mb-4">
              <Button
                variant={flagsSubTab === "tenant" ? "default" : "ghost"}
                size="sm"
                onClick={() => setFlagsSubTab("tenant")}
                className={flagsSubTab === "tenant" ? "bg-emerald-600" : ""}
              >
                <Building2 className="w-4 h-4 mr-2" />
                Flags Tenant {isSuperAdmin && "(Super Admin)"}
              </Button>
              <Button
                variant={flagsSubTab === "roles" ? "default" : "ghost"}
                size="sm"
                onClick={() => setFlagsSubTab("roles")}
                className={flagsSubTab === "roles" ? "bg-purple-600" : ""}
              >
                <Shield className="w-4 h-4 mr-2" />
                Flags par Rôle
              </Button>
            </div>
          )}

          {/* SOUS-ONGLET: Flags Tenant */}
          {(flagsSubTab === "tenant" || !isAdmin) && isSuperAdmin && (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Layers className="w-5 h-5 text-emerald-400" />
                  Activation Produit
                </CardTitle>
                <p className="text-sm text-zinc-500">Active ou désactive les hubs et modules pour ce tenant</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Affichage HIÉRARCHIQUE : Hub → modules (menus). Au lieu d'une liste
                    à plat par catégorie, chaque module est imbriqué sous SON hub →
                    on voit quoi activer et où. (Les features sans hub parent =
                    « Autres ».) */}
                {(() => {
                  const hubs = availableFeatures.filter(f => f.category === "hubs");
                  const childrenOf = (hubId) => availableFeatures.filter(f => f.category !== "hubs" && f.parent_hub === hubId);
                  const orphans = availableFeatures.filter(f => f.category !== "hubs" && !hubs.some(h => h.id === f.parent_hub));

                  const renderRow = (feature, child = false) => {
                    const isEnabled = featureFlags[feature.id] ?? feature.default;
                    return (
                      <div
                        key={feature.id}
                        className={`flex items-center justify-between rounded-lg border transition-all ${child ? 'p-3' : 'p-4'} ${
                          isEnabled ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-zinc-800/50 border-zinc-700'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`${child ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg flex items-center justify-center ${isEnabled ? 'bg-emerald-500/20' : 'bg-zinc-700'}`}>
                            {isEnabled ? <ToggleRight className="w-5 h-5 text-emerald-400" /> : <ToggleLeft className="w-5 h-5 text-zinc-500" />}
                          </div>
                          <div>
                            <p className={`font-medium ${child ? 'text-sm' : ''} ${isEnabled ? 'text-white' : 'text-zinc-500'}`}>{feature.name}</p>
                            <p className="text-xs text-zinc-500">{feature.description}</p>
                          </div>
                        </div>
                        <Switch checked={isEnabled} onCheckedChange={(checked) => handleToggleFlag(feature.id, checked)} />
                      </div>
                    );
                  };

                  return (
                    <>
                      {hubs.map(hub => {
                        const kids = childrenOf(hub.id);
                        return (
                          <div key={hub.id} className="rounded-xl border border-zinc-700/60 p-3 space-y-2">
                            {renderRow(hub)}
                            <div className="ml-5 pl-4 border-l border-zinc-700 space-y-2">
                              {kids.length === 0
                                ? <p className="text-xs text-zinc-600 py-1">Aucun module</p>
                                : kids.map(f => renderRow(f, true))}
                            </div>
                          </div>
                        );
                      })}
                      {orphans.length > 0 && (
                        <div className="rounded-xl border border-zinc-700/60 p-3 space-y-2">
                          <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Autres</h3>
                          {orphans.map(f => renderRow(f))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* SOUS-ONGLET: Flags par Rôle */}
          {flagsSubTab === "roles" && isAdmin && (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-400" />
                  Feature Flags par Rôle
                </CardTitle>
                <p className="text-sm text-zinc-500">
                  Limitez l'accès aux modules pour chaque rôle. Un module désactivé masque automatiquement ses sous-modules.
                </p>
              </CardHeader>
              <CardContent>
                {/* Sélection du rôle */}
                <div className="mb-6">
                  <Label className="text-zinc-400 mb-2 block">Sélectionner un rôle</Label>
                  <div className="flex gap-2 flex-wrap">
                    {roles.filter(r => r.id !== 'super_admin').map(rôle => (
                      <Button
                        key={rôle.id}
                        variant={selectedRoleForFlags === rôle.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedRoleForFlags(rôle.id)}
                        className={selectedRoleForFlags === rôle.id ? "bg-purple-600" : "border-zinc-600"}
                      >
                        <Shield className="w-4 h-4 mr-1" />
                        {rôle.name}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Features du rôle sélectionné */}
                {selectedRoleForFlags && roleFeatures[selectedRoleForFlags] && (
                  <div className="space-y-4">
                    {roleFeatures[selectedRoleForFlags].inherited && (
                      <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-400 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        Ce rôle hérite des flags du tenant. Modifiez pour personnaliser.
                      </div>
                    )}

                    {["hubs", "modules", "ia"].map(category => {
                      const categoryFeatures = availableFeatures.filter(f => f.category === category);
                      if (categoryFeatures.length === 0) return null;
                      
                      const categoryLabels = {
                        hubs: "Hubs",
                        modules: "Modules",
                        ia: "Intelligence Artificielle"
                      };

                      return (
                        <div key={category}>
                          <h3 className="text-sm font-medium text-zinc-400 mb-3 uppercase tracking-wider">
                            {categoryLabels[category]}
                          </h3>
                          <div className="grid gap-2">
                            {categoryFeatures.map(feature => {
                              const roleData = roleFeatures[selectedRoleForFlags];
                              const isEnabledForRole = roleData?.features?.[feature.id] ?? featureFlags[feature.id] ?? feature.default;
                              const isEnabledForTenant = featureFlags[feature.id] ?? feature.default;
                              const isDisabledAtTenantLevel = !isEnabledForTenant;

                              return (
                                <div 
                                  key={feature.id}
                                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                    isDisabledAtTenantLevel
                                      ? 'bg-zinc-800/30 border-zinc-700/50 opacity-50'
                                      : isEnabledForRole 
                                        ? 'bg-purple-500/5 border-purple-500/30' 
                                        : 'bg-zinc-800/50 border-zinc-700'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded flex items-center justify-center ${
                                      isEnabledForRole ? 'bg-purple-500/20' : 'bg-zinc-700'
                                    }`}>
                                      {isEnabledForRole ? (
                                        <Check className="w-4 h-4 text-purple-400" />
                                      ) : (
                                        <X className="w-4 h-4 text-zinc-500" />
                                      )}
                                    </div>
                                    <div>
                                      <p className={`text-sm font-medium ${isEnabledForRole ? 'text-white' : 'text-zinc-500'}`}>
                                        {feature.name}
                                      </p>
                                      {isDisabledAtTenantLevel && (
                                        <p className="text-xs text-zinc-600">Désactivé au niveau Tenant</p>
                                      )}
                                    </div>
                                  </div>
                                  <Switch
                                    checked={isEnabledForRole}
                                    onCheckedChange={(checked) => toggleRoleFeature(selectedRoleForFlags, feature.id, checked)}
                                    disabled={isDisabledAtTenantLevel}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!selectedRoleForFlags && (
                  <div className="text-center py-8 text-zinc-500">
                    Sélectionnez un rôle pour gérer ses feature flags
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ========== BLOC 2: RÔLES ========== */}
      {activeTab === "roles" && (
        <div className="space-y-4">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-400" />
                Rôles du Tenant
              </CardTitle>
              <p className="text-sm text-zinc-500">Définissez les profils et leurs permissions par défaut</p>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {roles.map(rôle => (
                  <div 
                    key={rôle.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-zinc-800/50 border border-white/10 hover:border-purple-500/30 transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center`}>
                        <Shield className={`w-5 h-5 ${rôle.color || 'text-purple-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${rôle.color || 'text-white'}`}>{rôle.name}</p>
                          {rôle.is_system && (
                            <Badge className="bg-zinc-700 text-zinc-400 text-xs">Système</Badge>
                          )}
                        </div>
                        <p className="text-sm text-zinc-500">{rôle.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { loadRolePermissions(rôle.id); setActiveTab("permissions"); }}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Permissions
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ========== BLOC 3: PERMISSIONS (Matrice) ========== */}
      {activeTab === "permissions" && (
        <div className="space-y-4">
          {/* Sélecteur de rôle */}
          <Card className="bg-zinc-900/50 border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-zinc-400">Rôle :</span>
                  <Select value={selectedRole || ""} onValueChange={loadRolePermissions}>
                    <SelectTrigger className="w-48 bg-zinc-800 border-white/10">
                      <SelectValue placeholder="Sélectionner un rôle" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-white/10">
                      {roles.map(rôle => (
                        <SelectItem key={rôle.id} value={rôle.id}>
                          <span className={rôle.color}>{rôle.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedRole && (
                  <div className="flex items-center gap-2">
                    {editingPermissions ? (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => { setEditingPermissions(false); loadRolePermissions(selectedRole); }}>
                          <X className="w-4 h-4 mr-2" />
                          Annuler
                        </Button>
                        <Button size="sm" onClick={saveRolePermissions} className="bg-blue-500 hover:bg-blue-600">
                          <Save className="w-4 h-4 mr-2" />
                          Enregistrer
                        </Button>
                      </>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => setEditingPermissions(true)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Matrice des permissions */}
          {selectedRole && (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-400" />
                  Matrice des Permissions
                </CardTitle>
                <p className="text-sm text-zinc-500">
                  {rolePermissions === "all" || rolePermissions === "all_tenant" 
                    ? "Ce rôle a toutes les permissions" 
                    : "Définissez les actions autorisées par hub et module"}
                </p>
              </CardHeader>
              <CardContent>
                {rolePermissions === "all" || rolePermissions === "all_tenant" ? (
                  <div className="text-center py-8">
                    <Check className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                    <p className="text-emerald-400 font-medium">Toutes les permissions sont accordées</p>
                    <p className="text-zinc-500 text-sm mt-1">
                      {rolePermissions === "all" ? "Accès multi-tenant complet" : "Accès complet au tenant"}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto oh-responsive-table">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Hub</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Module</th>
                          {ACTIONS.map(action => (
                            <th key={action} className="text-center py-3 px-2 text-zinc-400 font-medium text-xs">
                              {ACTION_LABELS[action]}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(hubsDefinition).map(([hubId, hub]) => (
                          Object.entries(hub.modules || {}).map(([moduleId, moduleName], idx) => (
                            <tr key={`${hubId}-${moduleId}`} className="border-b border-white/5 hover:bg-zinc-900/50/5">
                              {idx === 0 && (
                                <td 
                                  className="py-3 px-4 text-white font-medium"
                                  rowSpan={Object.keys(hub.modules).length}
                                >
                                  {hub.name}
                                </td>
                              )}
                              <td className="py-3 px-4 text-zinc-300">{moduleName}</td>
                              {ACTIONS.map(action => (
                                <td key={action} className="text-center py-3 px-2">
                                  <Checkbox
                                    checked={hasPermission(hubId, moduleId, action)}
                                    onCheckedChange={() => editingPermissions && togglePermission(hubId, moduleId, action)}
                                    disabled={!editingPermissions}
                                    className={editingPermissions ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ========== BLOC 4: SURCHARGES UTILISATEUR ========== */}
      {activeTab === "overrides" && (
        <div className="space-y-4">
          {/* Sélecteur d'utilisateur */}
          <Card className="bg-zinc-900/50 border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-zinc-400">Utilisateur :</span>
                  <Select 
                    value={selectedUserForOverride?.user_id || selectedUserForOverride?.email || ""} 
                    onValueChange={loadUserOverrides}
                  >
                    <SelectTrigger className="w-64 bg-zinc-800 border-white/10">
                      <SelectValue placeholder="Sélectionner un utilisateur" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-white/10">
                      {users.filter(u => u.rôle !== "super_admin").map(user => (
                        <SelectItem key={user.user_id || user.email} value={user.user_id || user.email}>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>{user.name || user.email}</span>
                            <Badge className={`ml-2 text-xs ${ROLE_COLORS[user.rôle] || ""}`}>
                              {user.rôle}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedUserForOverride && (
                  <div className="flex items-center gap-2">
                    {editingOverrides ? (
                      <>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => { 
                            setEditingOverrides(false); 
                            loadUserOverrides(selectedUserForOverride.user_id || selectedUserForOverride.email); 
                          }}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Annuler
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={clearAllUserOverrides}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Tout supprimer
                        </Button>
                      </>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => setEditingOverrides(true)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                    )}
                  </div>
                )}
              </div>
              
              {selectedUserForOverride && (
                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-sm text-amber-400">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Les surcharges s'ajoutent aux permissions du rôle "{selectedUserForOverride.rôle}". 
                    Cochez une action pour l'ajouter, même si le rôle ne l'a pas.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Matrice des surcharges */}
          {selectedUserForOverride && (
            <Card className="bg-zinc-900/50 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <UserCog className="w-5 h-5 text-amber-400" />
                  Surcharges pour {selectedUserForOverride.name || selectedUserForOverride.email}
                </CardTitle>
                <p className="text-sm text-zinc-500">
                  Permissions supplémentaires accordées à cet utilisateur
                </p>
              </CardHeader>
              <CardContent>
                {Object.keys(userOverrides).length === 0 && !editingOverrides ? (
                  <div className="text-center py-8">
                    <UserCog className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-500">Aucune surcharge définie</p>
                    <p className="text-zinc-600 text-sm mt-1">
                      Cet utilisateur utilise uniquement les permissions de son rôle
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-4"
                      onClick={() => setEditingOverrides(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter des surcharges
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto oh-responsive-table">
                    <table className="w-full min-w-[640px]">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Hub</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Module</th>
                          {ACTIONS.map(action => (
                            <th key={action} className="text-center py-3 px-2 text-zinc-400 font-medium text-xs">
                              {ACTION_LABELS[action]}
                            </th>
                          ))}
                          {editingOverrides && (
                            <th className="text-center py-3 px-2 text-zinc-400 font-medium text-xs">
                              Actions
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(hubsDefinition).map(([hubId, hub]) => (
                          Object.entries(hub.modules || {}).map(([moduleId, moduleName], idx) => (
                            <tr key={`${hubId}-${moduleId}`} className="border-b border-white/5 hover:bg-zinc-900/50/5">
                              {idx === 0 && (
                                <td 
                                  className="py-3 px-4 text-white font-medium"
                                  rowSpan={Object.keys(hub.modules).length}
                                >
                                  {hub.name}
                                </td>
                              )}
                              <td className="py-3 px-4 text-zinc-300">{moduleName}</td>
                              {ACTIONS.map(action => (
                                <td key={action} className="text-center py-3 px-2">
                                  <Checkbox
                                    checked={isActionOverridden(hubId, moduleId, action)}
                                    onCheckedChange={() => {
                                      if (editingOverrides) {
                                        toggleOverrideAction(hubId, moduleId, action);
                                        saveUserOverride(hubId, moduleId, 
                                          isActionOverridden(hubId, moduleId, action)
                                            ? (userOverrides[hubId]?.[moduleId] || []).filter(a => a !== action)
                                            : [...(userOverrides[hubId]?.[moduleId] || []), action]
                                        );
                                      }
                                    }}
                                    disabled={!editingOverrides}
                                    className={`border-amber-500/50 ${isActionOverridden(hubId, moduleId, action) ? "bg-amber-500 text-white" : ""}`}
                                  />
                                </td>
                              ))}
                              {editingOverrides && userOverrides[hubId]?.[moduleId]?.length > 0 && (
                                <td className="text-center py-3 px-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => clearUserOverrideModule(hubId, moduleId)}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ========== DIALOG EDITION UTILISATEUR ========== */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingUser?._id ? "Modifier l'utilisateur" : "Nouvel utilisateur"}
            </DialogTitle>
          </DialogHeader>
          
          {editingUser && (
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-400">Nom *</Label>
                <Input
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="bg-zinc-800 border-white/10 mt-1"
                  placeholder="Nom complet"
                />
              </div>
              
              <div>
                <Label className="text-zinc-400">Email * <span className="text-zinc-500">(Login)</span></Label>
                <Input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className="bg-zinc-800 border-white/10 mt-1"
                  placeholder="email@example.com"
                />
              </div>
              
              <div>
                <Label className="text-zinc-400">Téléphone</Label>
                <Input
                  value={editingUser.phone || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                  className="bg-zinc-800 border-white/10 mt-1"
                  placeholder="+33 6 00 00 00 00"
                />
              </div>
              
              <div>
                <Label className="text-zinc-400">N° RCS <span className="text-zinc-500">(anti-doublon installateur)</span></Label>
                <Input
                  value={editingUser.rcs || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, rcs: e.target.value })}
                  className="bg-zinc-800 border-white/10 mt-1"
                  placeholder="Ex: 123 456 789 RCS Paris"
                  data-testid="gouv-user-rcs-input"
                />
                <p className="text-xs text-zinc-500 mt-1">Identifiant unique entreprise — bloque les doublons</p>
              </div>
              
              <div>
                <Label className="text-zinc-400">Rôle</Label>
                <Select 
                  value={editingUser.rôle} 
                  onValueChange={(v) => setEditingUser({ ...editingUser, rôle: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-white/10 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-white/10">
                    {/* Source unique des rôles (lib/roles.js) — voir fix R5 */}
                    {assignableRolesFor({ isSuperAdmin }).map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        <span className={r.colorClass}>{r.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Tenant — Super Admin: sélecteur, Admin: tenant courant implicite */}
              {isSuperAdmin ? (
                <div>
                  <Label className="text-zinc-400">Tenant *</Label>
                  <Select
                    value={editingUser.current_tenant || selectedTenantId || ""}
                    onValueChange={(v) => setEditingUser({ ...editingUser, current_tenant: v, tenants: [v] })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-white/10 mt-1" data-testid="user-tenant-select">
                      <SelectValue placeholder="Sélectionner un tenant" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-white/10">
                      {tenants.map(t => (
                        <SelectItem key={t.slug} value={t.slug}>
                          {t.nom || t.slug}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-zinc-800/50 border border-white/5">
                  <Shield className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-zinc-300">Tenant : <span className="font-medium text-white">{selectedTenantId}</span></span>
                </div>
              )}
              
              {/* Mot de passe */}
              <div>
                <Label className="text-zinc-400">
                  {editingUser._id ? "Nouveau mot de passe" : "Mot de passe"}
                </Label>
                <div className="flex gap-2 mt-1">
                  <div className="relative flex-1">
                    <Input
                      type={editingUser.showPassword ? "text" : "password"}
                      value={editingUser.password || ""}
                      onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                      className="bg-zinc-800 border-white/10 pr-10"
                      placeholder={editingUser._id ? "Laisser vide = inchangé" : "Mot de passe"}
                    />
                    <button
                      type="button"
                      onClick={() => setEditingUser({ ...editingUser, showPassword: !editingUser.showPassword })}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {editingUser.showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const generated = Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-4).toUpperCase() + "!";
                      setEditingUser({ ...editingUser, password: generated, showPassword: true });
                    }}
                    className="border-cyan-600 text-cyan-400 hover:bg-cyan-600/20 shrink-0"
                    title="Générer un mot de passe"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Liaison Installateur */}
              {installateurs.length > 0 && (
                <div>
                  <Label className="text-zinc-400">Lier à un installateur</Label>
                  <Select 
                    value={editingUser.installateur_id || "none"} 
                    onValueChange={(v) => {
                      if (v === "none") {
                        setEditingUser({ ...editingUser, installateur_id: "" });
                      } else {
                        // Auto-remplir les données depuis la fiche installateur
                        const inst = installateurs.find(i => (i.id || i._id) === v);
                        const updates = { installateur_id: v };
                        if (inst) {
                          // Pré-remplir si les champs user sont vides
                          if (!editingUser.name && inst.nom) updates.name = inst.nom;
                          if (!editingUser.phone && inst.telephone) updates.phone = inst.telephone;
                          if (!editingUser.rcs && inst.siret) updates.rcs = inst.siret;
                          if (!editingUser._id && !editingUser.email && inst.email) updates.email = inst.email;
                        }
                        setEditingUser({ ...editingUser, ...updates });
                      }
                    }}
                  >
                    <SelectTrigger className="bg-zinc-800 border-white/10 mt-1" data-testid="user-installateur-select">
                      <SelectValue placeholder="Aucun installateur" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-white/10">
                      <SelectItem value="none">Aucun installateur</SelectItem>
                      {installateurs.map(inst => (
                        <SelectItem key={inst.id || inst._id} value={inst.id || inst._id}>
                          <span>{inst.entreprise || inst.nom || inst.raison_sociale || "Installateur sans nom"}</span>
                          {inst.siret && <span className="text-purple-400 ml-2 text-xs">SIRET: {inst.siret}</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editingUser.installateur_id && editingUser.installateur_id !== "none" && (() => {
                    const linked = installateurs.find(i => (i.id || i._id) === editingUser.installateur_id);
                    return linked ? (
                      <div className="mt-2 p-2 rounded bg-cyan-500/10 border border-cyan-500/20 text-xs text-cyan-300">
                        <p className="font-medium">{linked.entreprise || linked.nom}</p>
                        {linked.siret && <p>SIRET: {linked.siret}</p>}
                        {linked.telephone && <p>Tel: {linked.telephone}</p>}
                        {linked.email && <p>Email: {linked.email}</p>}
                      </div>
                    ) : null;
                  })()}
                  <p className="text-xs text-zinc-500 mt-1">
                    Les données sont liées à la fiche installateur
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowUserDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveUser} className="bg-cyan-500 hover:bg-cyan-600">
              {editingUser?._id ? (
                <><Save className="w-4 h-4 mr-2" />Enregistrer</>
              ) : (
                <><Mail className="w-4 h-4 mr-2" />Envoyer l'invitation</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DIALOG HISTORIQUE DE CONNEXION ========== */}
      <Dialog open={!!historyUser} onOpenChange={(open) => { if (!open) { setHistoryUser(null); setHistoryData(null); } }}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-emerald-400" />
              Historique de connexion
            </DialogTitle>
            {historyUser && (
              <p className="text-sm text-zinc-400 mt-1">
                {historyUser.name || historyUser.email} — {historyUser.email}
              </p>
            )}
          </DialogHeader>

          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
            </div>
          ) : historyData ? (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-zinc-800/50 rounded-lg p-3 border border-white/5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Total tentatives</p>
                  <p className="text-2xl font-bold text-white mt-1">{historyData.total}</p>
                </div>
                <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
                  <p className="text-xs text-emerald-400 uppercase tracking-wide">Réussies</p>
                  <p className="text-2xl font-bold text-emerald-400 mt-1">{historyData.success_count}</p>
                </div>
                <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/20">
                  <p className="text-xs text-red-400 uppercase tracking-wide">Échouées</p>
                  <p className="text-2xl font-bold text-red-400 mt-1">{historyData.failure_count}</p>
                </div>
              </div>

              {/* Liste */}
              {historyData.items.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <LogIn className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>Aucune connexion enregistrée</p>
                </div>
              ) : (
                <div className="max-h-96 overflow-auto border border-white/5 rounded-lg">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-zinc-800/50 sticky top-0">
                      <tr className="text-left text-xs text-zinc-400 uppercase tracking-wide">
                        <th className="px-3 py-2">Date / Heure</th>
                        <th className="px-3 py-2">IP</th>
                        <th className="px-3 py-2">Source</th>
                        <th className="px-3 py-2">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {historyData.items.map((evt, idx) => {
                        const sourceLabel = {
                          password: "Login email",
                          google_sso: "Google SSO",
                          dal_mobile: "DAL Mobile",
                          impersonation: "Impersonation",
                        }[evt.source] || evt.source || "—";
                        const ctx = evt.context || {};
                        return (
                          <tr key={idx} className="hover:bg-zinc-800/30">
                            <td className="px-3 py-2 text-zinc-200 font-mono text-xs">{formatLoginTs(evt.ts)}</td>
                            <td className="px-3 py-2 text-zinc-400 font-mono text-xs">{evt.ip || "—"}</td>
                            <td className="px-3 py-2 text-xs">
                              <span className="text-zinc-300">{sourceLabel}</span>
                              {ctx.dossier_reference && (
                                <span className="block text-zinc-500 mt-0.5">
                                  Dossier {ctx.dossier_reference}{ctx.auth_method ? ` · ${ctx.auth_method}` : ""}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {evt.success ? (
                                <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">OK</Badge>
                              ) : (
                                <Badge className="bg-red-500/20 text-red-400 text-xs" title={evt.failure_reason || ""}>
                                  Échec
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setHistoryUser(null); setHistoryData(null); }}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
